import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { revealCallPhone } from '@/lib/calls-api';
import { getValidGraceToken } from '@/lib/phone-reveal-grace';
import { Modal } from '@/components/ui/modal';

/**
 * Issue #228 — 通话列表 / 详情「完整手机号」二次密码揭示。
 *
 * 默认展示脱敏手机号 + 「查看」按钮。点击时：
 *   - 当前会话已有有效 30 分钟 grace 凭证 → 直接揭示（免输密码）；
 *   - 否则弹出密码框，输入当前管理员登录密码，由后端 bcrypt 校验。
 * 仅 ADMIN / SUPER_ADMIN 路由可见此控件；密码错误后端返回 401，前端保留密码框并提示。
 * 明文仅在本组件内存展示，不写入任何持久层。
 */
const REVEAL_BTN =
  'inline-flex items-center gap-1 rounded-md border border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50';
const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function RevealPhoneCell({
  callId,
  phoneMasked,
  reason = '管理端查看完整客户手机号',
}: {
  callId: string;
  phoneMasked: string;
  reason?: string;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (pw: string | undefined) => revealCallPhone(callId, reason, pw),
    onSuccess: (data) => {
      setRevealed(data.phone);
      setAskPassword(false);
      setPassword('');
      setError(null);
    },
    onError: (err) => {
      // 401：grace 失效 / 密码错误 / 需要二次验证 → 弹出（或保留）密码框并提示。
      if (err instanceof ApiError && err.status === 401) {
        setError(password ? '管理员密码错误，请重试。' : '请输入当前管理员登录密码。');
        setAskPassword(true);
      } else {
        setError(err instanceof ApiError ? err.message : '查看失败，请稍后重试。');
      }
    },
  });

  if (revealed) {
    return <span className="font-mono text-slate-900">{revealed}</span>;
  }

  const onReveal = () => {
    setError(null);
    // 会话内已有有效 grace 凭证：免密直接揭示；否则弹密码框。
    if (getValidGraceToken()) {
      mutation.mutate(undefined);
    } else {
      setAskPassword(true);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-xs text-slate-500">{phoneMasked || '—'}</span>
      <button
        type="button"
        className={REVEAL_BTN}
        onClick={onReveal}
        disabled={mutation.isPending}
        data-testid="reveal-phone"
      >
        <Eye className="h-3 w-3" />
        {mutation.isPending ? '查看中…' : '查看'}
      </button>

      {askPassword ? (
        <Modal
          key={`reveal-${callId}`}
          title="查看完整手机号"
          description="出于隐私合规，查看完整手机号需二次验证当前管理员登录密码；验证通过后 30 分钟内本次会话免重复输入。"
          onClose={() => {
            setAskPassword(false);
            setError(null);
            setPassword('');
          }}
          footer={
            <>
              <button
                type="button"
                className={SECONDARY_BTN}
                onClick={() => {
                  setAskPassword(false);
                  setError(null);
                  setPassword('');
                }}
              >
                取消
              </button>
              <button
                type="submit"
                form={`reveal-form-${callId}`}
                className={PRIMARY_BTN}
                disabled={mutation.isPending || password.length === 0}
              >
                {mutation.isPending ? '验证中…' : '验证并查看'}
              </button>
            </>
          }
        >
          <form
            id={`reveal-form-${callId}`}
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              mutation.mutate(password);
            }}
          >
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">当前管理员登录密码</span>
              <input
                className={INPUT}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reveal-password"
              />
            </label>
            {error ? (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
                {error}
              </div>
            ) : null}
          </form>
        </Modal>
      ) : null}
    </span>
  );
}
